
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useSceneStore } from "../stores/sceneStore";
import { api } from "../api";
import type {
  TextureAsset,
  LightingConfig,
  ParticleType,
} from "@mind-sands/shared";
import { PARTICLE_TYPES } from "@mind-sands/shared";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Slider } from "@/components/ui/Slider";
import { LightsSection } from "./LightsSection";
import { meshEffectManager } from "../engine/effects/EffectManager";

/** Sensible UI bounds for environment config sliders/inputs.
 * These are UX constraints, not validation rules — the SceneDescriptor
 * from the server is already validated by the Space Compiler. */
const UI_RANGES = {
  terrain: {
    sizeMin: 5,
    sizeMax: 100,
    maxHeightMin: 0.5,
    maxHeightMax: 20,
    roughnessMin: 0,
    roughnessMax: 1,
    fadeWidthMin: 0.05,
    fadeWidthMax: 0.3,
  },
  sky: {
    starDensityMin: 0,
    starDensityMax: 1,
  },
  lighting: {
    ambientMin: 0,
    ambientMax: 1,
    hemisphereMin: 0,
    hemisphereMax: 1,
  },
  atmosphere: {
    fogNearMin: 1,
    fogNearMax: 200,
    fogFarMin: 10,
    fogFarMax: 500,
  },
} as const;

/** Clamp a number to [min, max] */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Validate hex color string, return fallback if invalid */
function validateHexColor(color: string, fallback: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(color) ? color : fallback;
}

/**
 * Compact labeled-row wrapper: label on the left, controls on the right.
 * Keeps the inspector panel dense and scannable.
 */
function LabeledRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <label className="text-[10px] font-medium text-sands-500 uppercase tracking-wider w-14 shrink-0">
        {label}
      </label>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

/**
 * Collapsible section for grouping config items.
 */
function Section({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="mb-2 border border-sands-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-sands-50 hover:bg-sands-100 transition-colors"
      >
        <span className="text-xs font-medium text-sands-700 uppercase tracking-wider">
          {title}
        </span>
        <svg
          className={`w-3 h-3 text-sands-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && <div className="p-3">{children}</div>}
    </div>
  );
}

/**
 * Debug info for the effect system — shows vegetation count and cache status.
 * Helps diagnose why wind effects may not appear on certain models.
 */
function EffectsInfo() {
  const objects = useSceneStore((state) => state.scene.objects);
  const [cacheReady, setCacheReady] = useState(
    () => meshEffectManager.cacheVersion > 0,
  );

  useEffect(() => {
    if (meshEffectManager.cacheVersion > 0) setCacheReady(true);
    return meshEffectManager.onCacheReady(() => setCacheReady(true));
  }, []);

  const vegetationCount = useMemo(
    () => objects.filter((o) => o.archetype === "vegetation").length,
    [objects],
  );

  const archetypes = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const obj of objects) {
      const a = obj.archetype ?? "none";
      counts[a] = (counts[a] ?? 0) + 1;
    }
    return counts;
  }, [objects]);

  return (
    <div className="space-y-2 text-xs text-sands-600">
      <div className="flex items-center justify-between">
        <span className="font-medium text-sands-500 uppercase tracking-wider">
          Cache
        </span>
        <span className={cacheReady ? "text-green-600" : "text-amber-500"}>
          {cacheReady ? "Loaded" : "Loading..."}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="font-medium text-sands-500 uppercase tracking-wider">
          Vegetation
        </span>
        <span>
          {vegetationCount} object{vegetationCount !== 1 ? "s" : ""}
        </span>
      </div>
      {Object.keys(archetypes).length > 0 && (
        <div className="border-t border-sands-100 pt-2 space-y-1">
          <span className="font-medium text-sands-500 uppercase tracking-wider">
            Archetypes
          </span>
          {Object.entries(archetypes).map(([key, count]) => (
            <div key={key} className="flex items-center justify-between pl-2">
              <span className="text-sands-500">{key}</span>
              <span className="text-sands-400">{count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Rendering debug panel — direct SceneDescriptor editing for development.
 * Allows editing terrain, sky, lighting, and atmosphere rendering parameters.
 *
 * This bypasses the Space Compiler and edits rendering params directly.
 * Only available in development mode.
 */
export function RenderingDebugPanel() {
  const scene = useSceneStore((state) => state.scene);
  const updateEnvironment = useSceneStore((state) => state.updateEnvironment);
  const resetEnvironment = useSceneStore((state) => state.resetEnvironment);

  const { terrain, sky, lighting, atmosphere } = scene.environment;

  // Texture list for dropdown
  const [textures, setTextures] = useState<TextureAsset[]>([]);

  // Fetch available textures on mount
  useEffect(() => {
    api
      .listTextures({ pageSize: 100 })
      .then((res) => setTextures(res.items))
      .catch((err) => console.warn("Failed to load textures:", err));
  }, []);

  // ── Terrain handlers ──────────────────────────────────────

  const handleSizeChange = useCallback(
    (axis: "width" | "depth", rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;

      const currentSize = terrain?.size ?? [24, 24];
      const clamped = clamp(
        parsed,
        UI_RANGES.terrain.sizeMin,
        UI_RANGES.terrain.sizeMax,
      );
      const newSize: [number, number] =
        axis === "width"
          ? [clamped, currentSize[1]]
          : [currentSize[0], clamped];

      updateEnvironment({
        terrain: { ...terrain!, size: newSize },
      });
    },
    [terrain, updateEnvironment],
  );

  const handleMaxHeightChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;

      const clamped = clamp(
        parsed,
        UI_RANGES.terrain.maxHeightMin,
        UI_RANGES.terrain.maxHeightMax,
      );

      updateEnvironment({
        terrain: { ...terrain!, maxHeight: clamped },
      });
    },
    [terrain, updateEnvironment],
  );

  const handleMaterialColorChange = useCallback(
    (color: string) => {
      const validColor = validateHexColor(color, "#d4c8a0");
      updateEnvironment({
        terrain: {
          ...terrain!,
          material: { ...terrain!.material, color: validColor },
        },
      });
    },
    [terrain, updateEnvironment],
  );

  const handleRoughnessChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;

      const clamped = clamp(
        parsed,
        UI_RANGES.terrain.roughnessMin,
        UI_RANGES.terrain.roughnessMax,
      );

      updateEnvironment({
        terrain: {
          ...terrain!,
          material: { ...terrain!.material, roughness: clamped },
        },
      });
    },
    [terrain, updateEnvironment],
  );

  const handleTextureChange = useCallback(
    (value: string) => {
      updateEnvironment({
        terrain: {
          ...terrain!,
          material: { ...terrain!.material, texture: value || undefined },
        },
      });
    },
    [terrain, updateEnvironment],
  );

  const handleFadeToggle = useCallback(
    (enabled: boolean) => {
      updateEnvironment({
        terrain: { ...terrain!, terrainFade: enabled },
      });
    },
    [terrain, updateEnvironment],
  );

  const handleFadeWidthChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;

      const clamped = clamp(
        parsed,
        UI_RANGES.terrain.fadeWidthMin,
        UI_RANGES.terrain.fadeWidthMax,
      );

      updateEnvironment({
        terrain: { ...terrain!, terrainFadeWidth: clamped },
      });
    },
    [terrain, updateEnvironment],
  );

  // ── Sky handlers ──────────────────────────────────────────

  const handleSkyTopColorChange = useCallback(
    (color: string) => {
      const validColor = validateHexColor(color, "#7cb5d4");
      updateEnvironment({
        sky: { ...sky!, topColor: validColor },
      });
    },
    [sky, updateEnvironment],
  );

  const handleSkyBottomColorChange = useCallback(
    (color: string) => {
      const validColor = validateHexColor(color, "#f5deb3");
      updateEnvironment({
        sky: { ...sky!, bottomColor: validColor },
      });
    },
    [sky, updateEnvironment],
  );

  const handleStarDensityChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;

      const clamped = clamp(
        parsed,
        UI_RANGES.sky.starDensityMin,
        UI_RANGES.sky.starDensityMax,
      );

      updateEnvironment({
        sky: { ...sky!, starDensity: clamped },
      });
    },
    [sky, updateEnvironment],
  );

  // ── Lighting handlers ─────────────────────────────────────

  const handleLightingUpdate = useCallback(
    (config: LightingConfig) => {
      updateEnvironment({ lighting: config });
    },
    [updateEnvironment],
  );

  // ── Atmosphere handlers ───────────────────────────────────

  const handleFogColorChange = useCallback(
    (color: string) => {
      const validColor = validateHexColor(color, "#e8d8c0");
      updateEnvironment({
        atmosphere: { ...atmosphere!, fogColor: validColor },
      });
    },
    [atmosphere, updateEnvironment],
  );

  const handleFogNearChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;

      const clamped = clamp(
        parsed,
        UI_RANGES.atmosphere.fogNearMin,
        UI_RANGES.atmosphere.fogNearMax,
      );

      updateEnvironment({
        atmosphere: { ...atmosphere!, fogNear: clamped },
      });
    },
    [atmosphere, updateEnvironment],
  );

  const handleFogFarChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;

      const clamped = clamp(
        parsed,
        UI_RANGES.atmosphere.fogFarMin,
        UI_RANGES.atmosphere.fogFarMax,
      );

      updateEnvironment({
        atmosphere: { ...atmosphere!, fogFar: clamped },
      });
    },
    [atmosphere, updateEnvironment],
  );

  const handleParticlesChange = useCallback(
    (value: string) => {
      updateEnvironment({
        atmosphere: { ...atmosphere!, particles: value as ParticleType },
      });
    },
    [atmosphere, updateEnvironment],
  );

  // ── Wind handlers ─────────────────────────────────────────

  const handleWindStrengthChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;
      const clamped = clamp(parsed, 0, 1);
      const currentWind = atmosphere!.wind ?? {
        direction: [1, 0] as [number, number],
        strength: 0,
      };
      updateEnvironment({
        atmosphere: {
          ...atmosphere!,
          wind: { ...currentWind, strength: clamped },
        },
      });
    },
    [atmosphere, updateEnvironment],
  );

  const handleWindDirXChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;
      const clamped = clamp(parsed, -1, 1);
      const currentWind = atmosphere!.wind ?? {
        direction: [1, 0] as [number, number],
        strength: 0.3,
      };
      updateEnvironment({
        atmosphere: {
          ...atmosphere!,
          wind: {
            ...currentWind,
            direction: [clamped, currentWind.direction[1]],
          },
        },
      });
    },
    [atmosphere, updateEnvironment],
  );

  const handleWindDirZChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;
      const clamped = clamp(parsed, -1, 1);
      const currentWind = atmosphere!.wind ?? {
        direction: [1, 0] as [number, number],
        strength: 0.3,
      };
      updateEnvironment({
        atmosphere: {
          ...atmosphere!,
          wind: {
            ...currentWind,
            direction: [currentWind.direction[0], clamped],
          },
        },
      });
    },
    [atmosphere, updateEnvironment],
  );

  const handleWindToggle = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        updateEnvironment({
          atmosphere: {
            ...atmosphere!,
            wind: { direction: [1, 0], strength: 0.3 },
          },
        });
      } else {
        const { wind: _wind, ...rest } = atmosphere!;
        updateEnvironment({ atmosphere: rest as typeof atmosphere });
      }
    },
    [atmosphere, updateEnvironment],
  );

  // ── Render ────────────────────────────────────────────────

  // Dev-only guard: render nothing in production (placed after all hooks)
  if (import.meta.env.PROD) return null;

  return (
    <div className="overflow-y-auto h-full p-2">
      {/* Reset button */}
      <button
        onClick={resetEnvironment}
        className="w-full px-3 py-2 mb-3 text-sm text-sands-600 bg-sands-50 hover:bg-sands-100 border border-sands-200 rounded-lg transition-colors"
      >
        Reset to Default
      </button>

      {/* Terrain section */}
      {terrain && (
        <Section title="Terrain">
          {/* Map size */}
          <LabeledRow label="Size">
            <div className="grid grid-cols-2 gap-1.5">
              <div className="flex items-center gap-1 bg-sands-50 border border-sands-200 rounded-lg pl-2 pr-1 py-1">
                <span className="text-xs text-sands-400 font-medium">W</span>
                <Input
                  type="number"
                  step="1"
                  min={UI_RANGES.terrain.sizeMin}
                  max={UI_RANGES.terrain.sizeMax}
                  value={terrain.size?.[0] ?? 24}
                  onChange={(e) => handleSizeChange("width", e.target.value)}
                  accent="none" surface="ghost" borderWeight="none" inputSize="xs" className="flex-1 min-w-0 text-sm pl-1 pr-0.5 py-0.5"
                />
              </div>
              <div className="flex items-center gap-1 bg-sands-50 border border-sands-200 rounded-lg pl-2 pr-1 py-1">
                <span className="text-xs text-sands-400 font-medium">D</span>
                <Input
                  type="number"
                  step="1"
                  min={UI_RANGES.terrain.sizeMin}
                  max={UI_RANGES.terrain.sizeMax}
                  value={terrain.size?.[1] ?? 24}
                  onChange={(e) => handleSizeChange("depth", e.target.value)}
                  accent="none" surface="ghost" borderWeight="none" inputSize="xs" className="flex-1 min-w-0 text-sm pl-1 pr-0.5 py-0.5"
                />
              </div>
            </div>
          </LabeledRow>

          {/* Max height */}
          <LabeledRow label="Height">
            <div className="flex items-center gap-1 bg-sands-50 border border-sands-200 rounded-lg pl-2 pr-1 py-1">
              <Input
                type="number"
                step="0.5"
                min={UI_RANGES.terrain.maxHeightMin}
                max={UI_RANGES.terrain.maxHeightMax}
                value={terrain.maxHeight ?? 2.5}
                onChange={(e) => handleMaxHeightChange(e.target.value)}
                accent="none" surface="ghost" borderWeight="none" inputSize="xs" className="flex-1 min-w-0 text-sm pl-1 pr-0.5 py-0.5"
              />
            </div>
          </LabeledRow>

          {/* Material color */}
          <LabeledRow label="Color">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={terrain.material.color}
                onChange={(e) => handleMaterialColorChange(e.target.value)}
                className="w-8 h-8 rounded border border-sands-200 cursor-pointer"
              />
              <Input
                type="text"
                value={terrain.material.color}
                onChange={(e) => handleMaterialColorChange(e.target.value)}
                accent="healing" surface="muted" borderWeight="thin" inputSize="sm" mono className="flex-1 text-sm"
              />
            </div>
          </LabeledRow>

          {/* Roughness */}
          <LabeledRow label="Roughness">
            <div className="flex items-center gap-2">
              <Slider
                min={UI_RANGES.terrain.roughnessMin}
                max={UI_RANGES.terrain.roughnessMax}
                step="0.05"
                value={terrain.material.roughness}
                onChange={(e) => handleRoughnessChange(e.target.value)}
              />
              <span className="text-xs text-sands-500 w-8 text-right">
                {terrain.material.roughness.toFixed(2)}
              </span>
            </div>
          </LabeledRow>

          {/* Texture */}
          <LabeledRow label="Texture">
            <Select
              value={terrain.material.texture ?? ""}
              onChange={(e) => handleTextureChange(e.target.value)}
            >
              <option value="">None</option>
              {textures.map((tex) => (
                <option key={tex.id} value={tex.id}>
                  {tex.name}
                </option>
              ))}
            </Select>
          </LabeledRow>

          {/* Fade Effect */}
          <LabeledRow label="Fade">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={terrain.terrainFade !== false}
                onChange={(e) => handleFadeToggle(e.target.checked)}
                className="w-4 h-4 rounded border-sands-300 accent-healing-500"
              />
              <span className="text-xs text-sands-500">Enable edge fade</span>
            </div>
          </LabeledRow>

          {/* Fade Width (only shown when fade is enabled) */}
          {terrain.terrainFade !== false && (
            <LabeledRow label="Fade Width">
              <div className="flex items-center gap-2">
                <Slider
                  min={UI_RANGES.terrain.fadeWidthMin}
                  max={UI_RANGES.terrain.fadeWidthMax}
                  step="0.01"
                  value={terrain.terrainFadeWidth ?? 0.15}
                  onChange={(e) => handleFadeWidthChange(e.target.value)}
                />
                <span className="text-xs text-sands-500 w-8 text-right">
                  {(terrain.terrainFadeWidth ?? 0.15).toFixed(2)}
                </span>
              </div>
            </LabeledRow>
          )}
        </Section>
      )}

      {/* Sky section */}
      {sky && (
        <Section title="Sky">
          {/* Top color */}
          <LabeledRow label="Top">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={sky.topColor}
                onChange={(e) => handleSkyTopColorChange(e.target.value)}
                className="w-8 h-8 rounded border border-sands-200 cursor-pointer"
              />
              <Input
                type="text"
                value={sky.topColor}
                onChange={(e) => handleSkyTopColorChange(e.target.value)}
                accent="healing" surface="muted" borderWeight="thin" inputSize="sm" mono className="flex-1 text-sm"
              />
            </div>
          </LabeledRow>

          {/* Bottom color */}
          <LabeledRow label="Bottom">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={sky.bottomColor}
                onChange={(e) => handleSkyBottomColorChange(e.target.value)}
                className="w-8 h-8 rounded border border-sands-200 cursor-pointer"
              />
              <Input
                type="text"
                value={sky.bottomColor}
                onChange={(e) => handleSkyBottomColorChange(e.target.value)}
                accent="healing" surface="muted" borderWeight="thin" inputSize="sm" mono className="flex-1 text-sm"
              />
            </div>
          </LabeledRow>

          {/* Star density (only for starry type) */}
          {sky.type === "starry" && (
            <LabeledRow label="Stars">
              <div className="flex items-center gap-2">
                <Slider
                  min={UI_RANGES.sky.starDensityMin}
                  max={UI_RANGES.sky.starDensityMax}
                  step="0.05"
                  value={sky.starDensity ?? 0.5}
                  onChange={(e) => handleStarDensityChange(e.target.value)}
                />
                <span className="text-xs text-sands-500 w-8 text-right">
                  {(sky.starDensity ?? 0.5).toFixed(2)}
                </span>
              </div>
            </LabeledRow>
          )}
        </Section>
      )}

      {/* Lighting section */}
      <LightsSection lighting={lighting} onUpdate={handleLightingUpdate} />

      {/* Atmosphere section */}
      {atmosphere && (
        <Section title="Atmosphere">
          {/* Fog color */}
          <LabeledRow label="Fog Color">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={atmosphere.fogColor}
                onChange={(e) => handleFogColorChange(e.target.value)}
                className="w-8 h-8 rounded border border-sands-200 cursor-pointer"
              />
              <Input
                type="text"
                value={atmosphere.fogColor}
                onChange={(e) => handleFogColorChange(e.target.value)}
                accent="healing" surface="muted" borderWeight="thin" inputSize="sm" mono className="flex-1 text-sm"
              />
            </div>
          </LabeledRow>

          {/* Fog near */}
          <LabeledRow label="Fog Near">
            <div className="flex items-center gap-1 bg-sands-50 border border-sands-200 rounded-lg pl-2 pr-1 py-1">
              <Input
                type="number"
                step="1"
                min={UI_RANGES.atmosphere.fogNearMin}
                max={UI_RANGES.atmosphere.fogNearMax}
                value={atmosphere.fogNear}
                onChange={(e) => handleFogNearChange(e.target.value)}
                accent="none" surface="ghost" borderWeight="none" inputSize="xs" className="flex-1 min-w-0 text-sm pl-1 pr-0.5 py-0.5"
              />
            </div>
          </LabeledRow>

          {/* Fog far */}
          <LabeledRow label="Fog Far">
            <div className="flex items-center gap-1 bg-sands-50 border border-sands-200 rounded-lg pl-2 pr-1 py-1">
              <Input
                type="number"
                step="1"
                min={UI_RANGES.atmosphere.fogFarMin}
                max={UI_RANGES.atmosphere.fogFarMax}
                value={atmosphere.fogFar}
                onChange={(e) => handleFogFarChange(e.target.value)}
                accent="none" surface="ghost" borderWeight="none" inputSize="xs" className="flex-1 min-w-0 text-sm pl-1 pr-0.5 py-0.5"
              />
            </div>
          </LabeledRow>

          {/* Particles */}
          <LabeledRow label="Particles">
            <Select
              value={atmosphere.particles ?? "none"}
              onChange={(e) => handleParticlesChange(e.target.value)}
            >
              {PARTICLE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </Select>
          </LabeledRow>

          {/* Wind toggle */}
          <LabeledRow label="Wind">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={atmosphere.wind !== undefined}
                onChange={(e) => handleWindToggle(e.target.checked)}
                className="w-4 h-4 rounded border-sands-300 accent-healing-500"
              />
              <span className="text-xs text-sands-500">Enable wind</span>
            </div>
          </LabeledRow>

          {/* Wind controls (only shown when wind is enabled) */}
          {atmosphere.wind && (
            <>
              {/* Wind strength */}
              <LabeledRow label="Force">
                <div className="flex items-center gap-2">
                  <Slider
                    min={0}
                    max={1}
                    step="0.05"
                    value={atmosphere.wind.strength}
                    onChange={(e) => handleWindStrengthChange(e.target.value)}
                  />
                  <span className="text-xs text-sands-500 w-8 text-right">
                    {atmosphere.wind.strength.toFixed(2)}
                  </span>
                </div>
              </LabeledRow>

              {/* Wind direction */}
              <LabeledRow label="Dir">
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="flex items-center gap-1 bg-sands-50 border border-sands-200 rounded-lg pl-2 pr-1 py-1">
                    <span className="text-xs text-sands-400 font-medium">
                      X
                    </span>
                    <Input
                      type="number"
                      step="0.1"
                      min={-1}
                      max={1}
                      value={atmosphere.wind.direction[0]}
                      onChange={(e) => handleWindDirXChange(e.target.value)}
                      accent="none" surface="ghost" borderWeight="none" inputSize="xs" className="flex-1 min-w-0 text-sm pl-1 pr-0.5 py-0.5"
                    />
                  </div>
                  <div className="flex items-center gap-1 bg-sands-50 border border-sands-200 rounded-lg pl-2 pr-1 py-1">
                    <span className="text-xs text-sands-400 font-medium">
                      Z
                    </span>
                    <Input
                      type="number"
                      step="0.1"
                      min={-1}
                      max={1}
                      value={atmosphere.wind.direction[1]}
                      onChange={(e) => handleWindDirZChange(e.target.value)}
                      accent="none" surface="ghost" borderWeight="none" inputSize="xs" className="flex-1 min-w-0 text-sm pl-1 pr-0.5 py-0.5"
                    />
                  </div>
                </div>
              </LabeledRow>
            </>
          )}
        </Section>
      )}

      {/* Effects debug section */}
      <Section title="Effects" defaultOpen={false}>
        <EffectsInfo />
      </Section>
    </div>
  );
}
